import { z } from 'zod';
import { PaymentTransactionStatus } from '@pillar-portal/database/dist/frontend';
import { AddressSchema, BusinessDetailsSchema, type Address } from '../backend_config.types';
import {
    JsonNonnegativeLosslessNumberInputSchema,
    JsonPercentageLosslessNumberInputSchema,
    JsonValueSchema,
} from './common.schemas';
import {
    GenericTransactionDetailsSchema,
    PaymentTransactionAmountBreakdownSchema,
    TokenizationResultSchema,
} from './payment.schemas';
import { IpgCardTokenizationResponseSchema } from './ipg/ipg-card-tokenization-response-registry.schemas';
import { GatewayLifecycleRequestSchema } from './ipg/gateway-lifecycle-request.schemas';
import {
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
} from './ipw/dealertrack/dealertrack-transaction-details.schemas';
import { StoredMaskedPanSchema } from './masked_pan.schemas';
import {
    CustomerContactRequiredErrorMessage,
    CustomerDetailsSchema,
    customerDetailsHasContact,
} from './customer-details.schemas';
import { MerchantTimeZoneSchema } from '../timezone.utils';
import {
    CardType,
    type GetReceiptDataResponse,
    type ProcessAchPaymentRequest,
    type ProcessAchPaymentResponse,
    type ProcessCardTokenPaymentRequest,
    type ProcessCardTokenPaymentResponse,
    type RecordTokenRequest,
    type RecordTokenResponse,
    type SambaGatewayTransactionDetailsFetchResult,
    type TerminalAbortRequest,
    type TerminalAbortResponse,
    type TerminalSaleRequest,
    type TerminalSaleResponse,
    type TransactionDetails,
    type TransactionDetailsCreateRequest,
    type GatewayCardTokenizationResponse,
} from '../samba-payment.types';
import { ReceiptTransactionType, TerminalCardEntryMode } from '../payment.types';

const UuidSchema: z.ZodString = z.string().uuid();

const NonEmptyStringSchema: z.ZodString = z.string().min(1);

export const CardholderBillingAddressSchema: z.ZodType<Address> = AddressSchema.superRefine(
    (value: Address, ctx: z.RefinementCtx) => {
        if (value.street.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing street is required',
                path: ['street'],
            });
        }
        if (value.city.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing city is required',
                path: ['city'],
            });
        }
        if (value.country === 'US' && value.zip === '00000') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Billing ZIP must not be 00000',
                path: ['zip'],
            });
        }
    }
);

export const BillingAddressSchema: z.ZodType<Address> = CardholderBillingAddressSchema;

export const SambaGatewayResponseHeaderSchema: z.ZodType<Record<string, string>> = z.record(
    z.string(),
    z.string()
);

export const CardTypeSchema: z.ZodType<CardType> = z.enum(CardType);

export const TransactionDetailsSchema: z.ZodType<TransactionDetails> = z.union([
    GenericTransactionDetailsSchema,
    DealertrackPartsTransactionDetailsV1Schema,
    DealertrackServiceTransactionDetailsV1Schema,
    DealertrackDealTransactionDetailsV1Schema,
    DealertrackLeasingTransactionDetailsV1Schema,
    DealertrackArStatementCustomersTransactionDetailsV1Schema,
]);

export { CustomerDetailsSchema };

const TransactionDetailsCreateRequestSchema: z.ZodType<
    TransactionDetailsCreateRequest<TransactionDetails>
> = z.lazy(() => TransactionDetailsSchema);

export const TerminalSaleRequestSchema: z.ZodType<TerminalSaleRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    terminal_card_entry_mode: z.enum(TerminalCardEntryMode),
    amount: z.string().optional(),
    description: z.string().optional(),
    customer_name: z.string().optional(),
    transaction_details: TransactionDetailsCreateRequestSchema,
    merchant_transaction_id: z.never().optional(),
});

export const TerminalSaleResponseSchema: z.ZodType<TerminalSaleResponse> = z.object({
    payment_transaction_uuid: UuidSchema.optional(),
    gateway_txn_id: z.string().optional(),
    success: z.boolean(),
    error: z.string().optional(),
    tokenization_result: TokenizationResultSchema.optional(),
});

export const TerminalAbortRequestSchema: z.ZodType<TerminalAbortRequest> = z.object({
    terminal_device_uuid: UuidSchema,
    merchant_transaction_id: z.string().min(1).optional(),
    query_only: z.boolean().optional(),
});

export const TerminalAbortResponseSchema: z.ZodType<TerminalAbortResponse> = z.object({
    success: z.boolean(),
    error: z.string().optional(),
    payment_transaction_uuid: UuidSchema.optional(),
    transaction_status: z.enum(PaymentTransactionStatus).optional(),
    gateway_txn_id: z.string().min(1).optional(),
});

export const GatewayCardTokenizationResponseSchema: z.ZodType<GatewayCardTokenizationResponse> =
    IpgCardTokenizationResponseSchema;

export const RecordTokenRequestSchema: z.ZodType<RecordTokenRequest> = z
    .object({
        customer_details: CustomerDetailsSchema,
        terminal_device_uuid: UuidSchema,
        stored_for_future_use: z.boolean().optional(),
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        masked_pan: StoredMaskedPanSchema,
        cardholder_name: NonEmptyStringSchema,
        expiry_date: z
            .string()
            .regex(/^\d{2}\/\d{2}$/)
            .optional(),
        gateway_response: GatewayCardTokenizationResponseSchema,
        transaction_details: TransactionDetailsCreateRequestSchema.optional(),
    })
    .refine(({ customer_details }) => customerDetailsHasContact(customer_details), {
        message: CustomerContactRequiredErrorMessage,
        path: ['customer_details'],
    });

export const RecordTokenResponseSchema: z.ZodType<RecordTokenResponse> = z.object({
    success: z.boolean(),
    payment_method_token_uuid: UuidSchema,
    payment_transaction_uuid: UuidSchema.optional(),
});

export const ProcessCardTokenPaymentRequestSchema: z.ZodType<ProcessCardTokenPaymentRequest> =
    z.object({
        terminal_device_uuid: UuidSchema,
        token: z.string().min(1),
        card_type: CardTypeSchema,
        card_brand: NonEmptyStringSchema,
        card_last4: z.string().regex(/^\d{4}$/),
        cardholder_name: NonEmptyStringSchema,
        billing_address: BillingAddressSchema.optional(),
        amount: z.string().optional(),
        description: z.string().optional(),
        transaction_details: TransactionDetailsCreateRequestSchema,
        surcharge_amount: JsonNonnegativeLosslessNumberInputSchema,
        convenience_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_fee_amount: JsonNonnegativeLosslessNumberInputSchema,
        total_amount_paid: JsonNonnegativeLosslessNumberInputSchema,
        merchant_transaction_id: z.never().optional(),
    });

export const ProcessCardTokenPaymentResponseSchema: z.ZodType<ProcessCardTokenPaymentResponse> =
    z.object({
        payment_transaction_uuid: UuidSchema.optional(),
        gateway_txn_id: z.string().optional(),
        success: z.boolean(),
        error: z.string().optional(),
        tokenization_result: TokenizationResultSchema.optional(),
    });

export const ProcessAchPaymentRequestSchema: z.ZodType<ProcessAchPaymentRequest> = z.object({
    transaction_details: TransactionDetailsCreateRequestSchema,
    gateway_txn_id: z.string().optional(),
    gateway_request: GatewayLifecycleRequestSchema.optional(),
    gateway_response: JsonValueSchema.optional(),
    gateway_response_status: z.number().int().optional(),
    gateway_response_header: SambaGatewayResponseHeaderSchema.optional(),
});

export const ProcessAchPaymentResponseSchema: z.ZodType<ProcessAchPaymentResponse> = z.object({
    success: z.boolean(),
    payment_transaction_uuid: UuidSchema,
});

export const SambaGatewayTransactionDetailsFetchResultSchema: z.ZodType<SambaGatewayTransactionDetailsFetchResult> =
    z.object({
        gateway_transaction_details: JsonValueSchema,
        gateway_response: JsonValueSchema,
        gateway_response_type: NonEmptyStringSchema,
        gateway_response_status: z.number().int(),
        gateway_response_header: SambaGatewayResponseHeaderSchema,
        gateway_endpoint_url: NonEmptyStringSchema.optional(),
    });

export { PaymentTransactionAmountBreakdownSchema };

export const GetReceiptDataResponseSchema: z.ZodType<GetReceiptDataResponse> = z.object({
    merchant_transaction_id: z.string(),
    transaction_type: z.enum(ReceiptTransactionType),
    receipt_timestamp: z.string(),
    merchant_timezone: MerchantTimeZoneSchema.nullable(),
    gateway_rrn: z.string().optional(),
    gateway_auth_code: z.string().optional(),
    card_brand: z.string().optional(),
    card_type: CardTypeSchema,
    card_last4: z.string().optional(),
    surcharge_pct: JsonPercentageLosslessNumberInputSchema.optional(),
    original_amount: z.string(),
    amount_breakdown: PaymentTransactionAmountBreakdownSchema,
    transactionDetails: z.lazy(() => TransactionDetailsSchema),
    business_details: BusinessDetailsSchema.optional(),
    can_get_receipt: z.boolean(),
    missing_required_fields: z.array(z.string()),
});
